import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, getListItems } from '../../services/api';
import { departureLabel, tourHasPackages, tourPriceDisplay } from '../../utils/text';

const emptyForm = {
  customer_name: '',
  customer_email: '',
  customer_phone: '',
  tour_id: '',
  departure_id: '',
  package_id: '',
  adults: '1',
  children: '0',
  message: '',
};

function GuestField({ id, label, hint, name, value, min, max, onChange }) {
  const numeric = Number(value) || 0;
  const setValue = (next) => {
    const clamped = Math.min(max, Math.max(min, next));
    onChange({ target: { name, value: String(clamped) } });
  };

  return (
    <div className="twa-booking-field">
      <label className="twa-booking-label" htmlFor={id}>
        {label}
        {hint ? <span className="twa-booking-hint">{hint}</span> : null}
      </label>
      <div className="twa-booking-stepper">
        <button
          type="button"
          className="twa-booking-stepper__btn"
          aria-label={`Decrease ${label}`}
          disabled={numeric <= min}
          onClick={() => setValue(numeric - 1)}
        >
          −
        </button>
        <input
          id={id}
          type="number"
          className="twa-booking-stepper__input"
          name={name}
          min={min}
          max={max}
          value={value}
          onChange={onChange}
          required={min > 0}
          inputMode="numeric"
        />
        <button
          type="button"
          className="twa-booking-stepper__btn"
          aria-label={`Increase ${label}`}
          disabled={numeric >= max}
          onClick={() => setValue(numeric + 1)}
        >
          +
        </button>
      </div>
    </div>
  );
}

/**
 * Booking form wired to CMS tours + POST /api/bookings.
 * Pass initialTourId to pre-select a tour (e.g. from tour details).
 */
function TourBookingForm({
  initialTourId = '',
  compact = false,
  lockTour = false,
  lockedTourTitle = '',
  departures: departuresProp,
  packages: packagesProp,
  adultMinAge: adultMinAgeProp,
  childMaxAge: childMaxAgeProp,
  source = 'website',
  title = 'Book Your Tour Slot',
}) {
  const [searchParams] = useSearchParams();
  const queryTourId = searchParams.get('tour_id') || '';
  const preselect = String(initialTourId || queryTourId || '');

  const [tours, setTours] = useState([]);
  const [form, setForm] = useState({ ...emptyForm, tour_id: preselect });
  const [status, setStatus] = useState({ type: '', text: '' });
  const [submitting, setSubmitting] = useState(false);
  const [loadingTours, setLoadingTours] = useState(true);

  const selectedTour = tours.find((t) => String(t.id) === String(form.tour_id));
  const tourName = (lockedTourTitle || selectedTour?.title || '').trim();
  const departures = useMemo(() => {
    if (Array.isArray(departuresProp) && departuresProp.length > 0) {
      return departuresProp;
    }
    return selectedTour?.departures || [];
  }, [departuresProp, selectedTour]);
  const packages = useMemo(() => {
    if (Array.isArray(packagesProp) && packagesProp.length > 0) {
      return packagesProp;
    }
    return selectedTour?.packages || [];
  }, [packagesProp, selectedTour]);
  const hasPackages = packages.length > 0 || tourHasPackages(selectedTour);
  const selectedPackage = packages.find((p) => String(p.id) === String(form.package_id));
  const selectedDeparture = departures.find((d) => String(d.id) === String(form.departure_id));
  const remaining = selectedDeparture ? Number(selectedDeparture.available_slots) || 0 : null;
  const guestTotal = (Number(form.adults) || 1) + (Number(form.children) || 0);
  const adultMinAge = Math.max(1, Number(selectedTour?.adult_min_age ?? adultMinAgeProp ?? 12) || 12);
  const childMaxAge = Math.max(1, Number(selectedTour?.child_max_age ?? childMaxAgeProp ?? 3) || 3);
  const summaryPrice = selectedPackage?.price
    || tourPriceDisplay(selectedTour)
    || '';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getTours({ per_page: 100 });
        if (!cancelled) {
          setTours(getListItems(res));
        }
      } catch {
        if (!cancelled) setTours([]);
      } finally {
        if (!cancelled) setLoadingTours(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (preselect) {
      setForm((prev) => ({ ...prev, tour_id: preselect }));
    }
  }, [preselect]);

  useEffect(() => {
    if (!departures.length) {
      setForm((prev) => (prev.departure_id ? { ...prev, departure_id: '' } : prev));
      return;
    }
    setForm((prev) => {
      const stillValid = departures.some((d) => String(d.id) === String(prev.departure_id) && !d.is_full);
      if (stillValid) {
        return prev;
      }
      const firstOpen = departures.find((d) => !d.is_full && Number(d.available_slots) > 0);
      return { ...prev, departure_id: firstOpen ? String(firstOpen.id) : '' };
    });
  }, [form.tour_id, departures]);

  useEffect(() => {
    if (!packages.length) {
      setForm((prev) => (prev.package_id ? { ...prev, package_id: '' } : prev));
      return;
    }
    setForm((prev) => {
      const stillValid = packages.some((p) => String(p.id) === String(prev.package_id));
      if (stillValid) {
        return prev;
      }
      return { ...prev, package_id: String(packages[0].id) };
    });
  }, [form.tour_id, packages]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.tour_id && !preselect) {
      setStatus({ type: 'error', text: 'Please select a tour.' });
      return;
    }
    if (departures.length > 0 && !form.departure_id) {
      setStatus({ type: 'error', text: 'Please select a tour date.' });
      return;
    }
    if (hasPackages && !form.package_id) {
      setStatus({ type: 'error', text: 'Please select a package.' });
      return;
    }
    if (remaining != null && guestTotal > remaining) {
      setStatus({ type: 'error', text: `Only ${remaining} slot${remaining === 1 ? '' : 's'} left on this date.` });
      return;
    }
    setSubmitting(true);
    setStatus({ type: '', text: '' });
    try {
      const payload = {
        customer_name: form.customer_name.trim(),
        customer_email: form.customer_email.trim(),
        customer_phone: form.customer_phone.trim(),
        tour_id: form.tour_id ? Number(form.tour_id) : (preselect ? Number(preselect) : null),
        departure_id: form.departure_id ? Number(form.departure_id) : null,
        package_id: form.package_id ? Number(form.package_id) : null,
        tour_type: tourName,
        source,
        adults: Number(form.adults) || 1,
        children: Number(form.children) || 0,
        message: form.message.trim(),
      };
      const response = await api.submitBooking(payload);
      setStatus({
        type: 'success',
        text: `Booking submitted! Reference: ${response.data?.booking_ref || ''}. We will confirm your slot soon.`,
      });
      setForm({ ...emptyForm, tour_id: preselect });
    } catch (err) {
      setStatus({ type: 'error', text: err.message || 'Booking failed. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={`twa-booking-form${compact ? ' twa-booking-form--compact' : ''}`}
    >
      <h3 className="twa-booking-form__title">{title}</h3>
      {tourName && (
        <div className="twa-booking-summary">
          <span className="twa-booking-summary__kicker">Tour</span>
          <strong>{tourName}</strong>
          {selectedTour && (selectedTour.duration_days || selectedTour.location || summaryPrice) && (
            <p>
              {[
                selectedTour.duration_days ? `${selectedTour.duration_days} Days` : null,
                selectedTour.location || null,
                summaryPrice
                  ? (selectedPackage
                    ? `${selectedPackage.title} · ${selectedPackage.price}`
                    : (tourHasPackages(selectedTour) ? summaryPrice : `${summaryPrice}/Person`))
                  : null,
              ].filter(Boolean).join(' · ')}
            </p>
          )}
          {!lockTour && selectedTour && (
            <Link to={`/tour/${selectedTour.id}`} className="twa-booking-summary__link">
              View full tour details
            </Link>
          )}
        </div>
      )}

      <div className="twa-booking-grid">
        <div className="twa-booking-field">
          <label className="twa-booking-label" htmlFor="booking-name">Full name</label>
          <input
            id="booking-name"
            type="text"
            className="twa-booking-input"
            name="customer_name"
            placeholder="Your full name"
            autoComplete="name"
            value={form.customer_name}
            onChange={handleChange}
            required
          />
        </div>
        <div className="twa-booking-field">
          <label className="twa-booking-label" htmlFor="booking-email">Email</label>
          <input
            id="booking-email"
            type="email"
            className="twa-booking-input"
            name="customer_email"
            placeholder="name@email.com"
            autoComplete="email"
            value={form.customer_email}
            onChange={handleChange}
            required
          />
        </div>
        <div className="twa-booking-field">
          <label className="twa-booking-label" htmlFor="booking-phone">Phone number</label>
          <input
            id="booking-phone"
            type="tel"
            className="twa-booking-input"
            name="customer_phone"
            placeholder="03xx xxxxxxx"
            autoComplete="tel"
            value={form.customer_phone}
            onChange={handleChange}
          />
        </div>

        {lockTour ? (
          <input type="hidden" name="tour_id" value={form.tour_id} />
        ) : (
          <div className="twa-booking-field twa-booking-field--full">
            <label className="twa-booking-label" htmlFor="booking-tour">Select a tour</label>
            <select
              id="booking-tour"
              className="twa-booking-input"
              name="tour_id"
              value={form.tour_id}
              onChange={handleChange}
              required
              disabled={loadingTours}
            >
              <option value="">{loadingTours ? 'Loading tours…' : 'Choose a tour'}</option>
              {tours.map((tour) => (
                <option key={tour.id} value={tour.id}>
                  {tour.title}
                  {tour.price ? ` — ${tour.price}` : ''}
                  {tour.duration_days ? ` (${tour.duration_days} Days)` : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        {packages.length > 0 && (
          <div className="twa-booking-field twa-booking-field--full">
            <label className="twa-booking-label" htmlFor="booking-package">Package</label>
            <select
              id="booking-package"
              className="twa-booking-input"
              name="package_id"
              value={form.package_id}
              onChange={handleChange}
              required
            >
              <option value="">Choose a package</option>
              {packages.map((pkg) => (
                <option key={pkg.id} value={pkg.id}>
                  {pkg.title}
                  {pkg.price ? ` — ${pkg.price}` : ''}
                  {pkg.description ? ` (${pkg.description})` : ''}
                </option>
              ))}
            </select>
            {selectedPackage?.description && (
              <p className="twa-booking-slots">{selectedPackage.description}</p>
            )}
          </div>
        )}

        <div className="twa-booking-field twa-booking-field--full">
          <label className="twa-booking-label" htmlFor="booking-date">Tour date</label>
          {departures.length === 0 ? (
            <p className="twa-booking-slots twa-booking-slots--wait">
              {form.tour_id ? 'Dates will appear here once they are published for this tour.' : 'Select a tour to see available dates.'}
            </p>
          ) : (
            <>
              <select
                id="booking-date"
                className="twa-booking-input"
                name="departure_id"
                value={form.departure_id}
                onChange={handleChange}
                required
              >
                <option value="">Choose a date</option>
                {departures.map((dep) => (
                  <option key={dep.id} value={dep.id} disabled={dep.is_full}>
                    {departureLabel(dep)}
                    {dep.is_full ? ' — Sold out' : ` — ${dep.available_slots} slot${dep.available_slots === 1 ? '' : 's'} left`}
                  </option>
                ))}
              </select>
              {selectedDeparture && (
                <p className={`twa-booking-slots${selectedDeparture.is_full ? ' twa-booking-slots--full' : ''}`}>
                  {selectedDeparture.is_full
                    ? 'This date is fully booked.'
                    : `${remaining} of ${selectedDeparture.total_slots} slots available`}
                </p>
              )}
            </>
          )}
        </div>
      </div>

      <div className="twa-booking-guests">
        <GuestField
          id="booking-adults"
          label="Adults"
          hint={`Age ${adultMinAge}+`}
          name="adults"
          value={form.adults}
          min={1}
          max={remaining != null ? Math.max(1, remaining) : 50}
          onChange={handleChange}
        />
        <GuestField
          id="booking-children"
          label="Children"
          hint={`Under ${childMaxAge}`}
          name="children"
          value={form.children}
          min={0}
          max={remaining != null ? Math.max(0, remaining - (Number(form.adults) || 1)) : 50}
          onChange={handleChange}
        />
      </div>

      <div className="twa-booking-field twa-booking-field--full">
        <label className="twa-booking-label" htmlFor="booking-notes">
          Special requests <span className="twa-booking-optional">(optional)</span>
        </label>
        <textarea
          id="booking-notes"
          name="message"
          rows={compact ? 3 : 4}
          className="twa-booking-input twa-booking-input--area"
          placeholder="Pickup, diet, or anything we should know"
          value={form.message}
          onChange={handleChange}
        />
      </div>

      <button
        type="submit"
        className={`twa-booking-submit${submitting ? ' is-busy' : ''}`}
        disabled={submitting || (remaining != null && remaining < 1)}
      >
        {submitting ? 'Submitting…' : remaining != null && remaining < 1 ? 'Sold Out' : 'Reserve My Slot'}
      </button>

      {status.text && (
        <p className={`twa-booking-status twa-booking-status--${status.type}`}>
          {status.text}
        </p>
      )}
      {!compact && (
        <p className="twa-booking-browse">
          Prefer browsing first?{' '}
          <Link to="/tour">See All Upcoming Tours</Link>
        </p>
      )}
    </form>
  );
}

export default TourBookingForm;
